import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import EquipmentCategoryGroup from './EquipmentCategoryGroup';
import { msalFetch } from '../auth/Auth-Utils';
import API from '../../constants/apiEndpoints';
import { Oval } from 'react-loader-spinner';
import { productPlaceholderImages } from '../../constants/constants';
import { IGasType } from './EquipmentDetail';

// Mock data interfaces
interface IEquipmentMetric {
    label: string;
    value: string;
    unit: number;
    gasType?: IGasType;
}

interface IEquipmentDevice {
    id: string;
    name: string;
    serialNumber?: string;
    category: string;
    imageUrl: string;
    building?: string;
    floor?: string;
    room?: string;
    metric?: IEquipmentMetric; // Optional: only present when latestSensorValues has data
    alarmCount?: number;
    hasAlert: boolean;
}

interface IEquipmentCategory {
    id: string;
    name: string;
    devices: IEquipmentDevice[];
    isCollapsed: boolean;
}

interface ConnectedEquipmentViewProps {
    t: TFunction;
    selectedFacilityID: string;
    selectedCategory: string;
    searchTerm?: string;
    selectedStatus?: string;
    selectedBuilding?: string;
    selectedFloor?: string;
    selectedZone?: string;
    hideDecommissionedAssets?: boolean;
}

const ConnectedEquipmentView: React.FC<ConnectedEquipmentViewProps> = ({
    t,
    selectedFacilityID,
    selectedCategory,
    searchTerm = '',
    selectedStatus = 'All',
    selectedBuilding = 'All',
    selectedFloor = 'All',
    selectedZone = 'All',
    hideDecommissionedAssets = false
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const history = useHistory();

    // Fetch dashboard equipment data from API
    const getDashboardEquipment = async (): Promise<IEquipmentCategory[]> => {
        if (!selectedFacilityID) {
            return [];
        }

        try {
            const response = await msalFetch(
                API.GET.facility.dashboardEquipment,
                {
                    method: 'get',
                    params: {
                        facilityId: selectedFacilityID,
                        search: searchTerm || undefined,
                        status:
                            selectedStatus !== 'All'
                                ? selectedStatus
                                : undefined,
                        buildingID:
                            selectedBuilding !== 'All'
                                ? selectedBuilding
                                : undefined,
                        floorID:
                            selectedFloor !== 'All' ? selectedFloor : undefined,
                        locationID:
                            selectedZone !== 'All' ? selectedZone : undefined,
                        hideDecommissioned:
                            hideDecommissionedAssets || undefined
                    }
                }
            );

            if (!response.data) {
                throw new Error('missing data');
            }

            // Transform flat API response into categorized structure
            const equipmentArray = response.data as any[];

            if (!Array.isArray(equipmentArray)) {
                return [];
            }

            // Group equipment by mainCategoryName
            const categoryMap = new Map<string, IEquipmentDevice[]>();

            equipmentArray.forEach((item: any) => {
                const categoryName = item.mainCategoryName || 'Uncategorized';

                // Use nickname first, fall back to productName
                let displayName = 'Unnamed';
                if (item.nickname) {
                    displayName = item.nickname;
                } else if (item.productName) {
                    const parts = item.productName
                        .split(':')
                        .map((p: string) => p.trim());
                    // Use the last part (description) if available, otherwise full productName
                    displayName =
                        parts.length >= 5
                            ? parts[parts.length - 1]
                            : item.productName;
                }

                // Get image - prefer base64 productImage, fall back to URL, then placeholder
                let imageUrl: string;
                if (item.productImage) {
                    // Use base64 image if available
                    imageUrl = `data:image/png;base64,${item.productImage}`;
                } else if (item.productImagePath) {
                    // Fall back to URL (may not work due to blob access)
                    imageUrl = item.productImagePath;
                } else {
                    // Use placeholder image based on mainCategoryID
                    const placeholder = productPlaceholderImages.find(
                        img =>
                            img.categoryId.toLowerCase() ===
                            item.mainCategoryID?.toLowerCase()
                    );
                    if (placeholder) {
                        try {
                            imageUrl = require(`../../images${
                                placeholder.src === 'Azure.png'
                                    ? ''
                                    : '/products'
                            }/${placeholder.src}`);
                        } catch (e) {
                            imageUrl = '/equipment-tile.png';
                        }
                    } else {
                        imageUrl = '/equipment-tile.png';
                    }
                }

                // Map API fields to UI model
                const device: IEquipmentDevice = {
                    id: item.installBaseID,
                    name: displayName,
                    serialNumber: item.serialNumber || undefined,
                    category: categoryName,
                    imageUrl,
                    building: item.buildingName || undefined,
                    floor: item.floorName || undefined,
                    room: item.roomName || undefined,
                    hasAlert: item.hasAlert || false
                };

                // Add metric if latestSensorValues has data or if gasType exists
                if (
                    (item.latestSensorValues &&
                        Array.isArray(item.latestSensorValues) &&
                        item.latestSensorValues.length > 0) ||
                    item.gasType
                ) {
                    // Use first sensor value if available
                    const sensor = item.latestSensorValues?.[0];
                    device.metric = {
                        label: sensor?.label || 'VALUE',
                        value: sensor?.value || 0,
                        unit: sensor?.unit || '',
                        gasType: item.gasType || undefined
                    };
                }

                // Add device to category group
                if (!categoryMap.has(categoryName)) {
                    categoryMap.set(categoryName, []);
                }
                const categoryDevices = categoryMap.get(categoryName);
                if (categoryDevices) {
                    categoryDevices.push(device);
                }
            });

            // Convert map to array of categories
            const categories: IEquipmentCategory[] = Array.from(
                categoryMap.entries()
            ).map(([name, devices]) => ({
                id: name.toLowerCase().replace(/\s+/g, '-'),
                name,
                devices,
                isCollapsed: false
            }));

            return categories;
        } catch (error) {
            console.error('Error fetching dashboard equipment:', error);
            return [];
        }
    };

    const { data: categoriesData, isLoading } = useQuery(
        [
            'connected-equipment',
            selectedFacilityID,
            searchTerm,
            selectedStatus,
            selectedBuilding,
            selectedFloor,
            selectedZone,
            hideDecommissionedAssets
        ],
        getDashboardEquipment,
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedFacilityID
        }
    );

    const [categories, setCategories] = useState<IEquipmentCategory[]>([]);

    // Update categories when data is fetched
    React.useEffect(() => {
        if (categoriesData) {
            setCategories(categoriesData);
        }
    }, [categoriesData]);

    // Filter categories based on selected category
    const filteredCategories = React.useMemo(() => {
        if (selectedCategory === 'All') {
            return categories;
        }

        // Filter to only show the selected category by name
        // selectedCategory is now the category name (not ID)
        return categories.filter(
            cat => cat.name.toLowerCase() === selectedCategory.toLowerCase()
        );
    }, [categories, selectedCategory]);

    // Calculate total device count from filtered categories
    const totalDeviceCount = filteredCategories.reduce(
        (sum, cat) => sum + cat.devices.length,
        0
    );

    const toggleCategoryCollapse = (categoryId: string) => {
        setCategories(prevCategories =>
            prevCategories.map(cat =>
                cat.id === categoryId
                    ? { ...cat, isCollapsed: !cat.isCollapsed }
                    : cat
            )
        );
    };

    const handleCardClick = (deviceId: string) => {
        history.push(`/dashboard/equipment-detail/${deviceId}`);
    };

    const hasData = totalDeviceCount > 0;

    if (isLoading) {
        return (
            <div className="connected-equipment-section loading-state">
                <div className="no-data-message loading-message">
                    <Oval
                        color="#ff9738"
                        secondaryColor="#ffcd9e"
                        height={60}
                        width={60}
                    />
                    <p>Loading equipment data...</p>
                </div>
            </div>
        );
    }

    if (!hasData) {
        return null;
    }

    return (
        <div className="connected-equipment-section">
            <div className="connected-equipment-header">
                <div className="header-title-container">
                    <h2>Connected Equipment View</h2>
                    <span className="device-count-badge">
                        {totalDeviceCount}
                    </span>
                </div>
                <Button
                    bsStyle="link"
                    className="collapse-button"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                >
                    {isCollapsed ? 'Expand' : 'Collapse'}
                    <FontAwesomeIcon
                        icon={
                            isCollapsed
                                ? ['fas', 'chevron-down']
                                : ['fas', 'chevron-up']
                        }
                        className="collapse-icon"
                    />
                </Button>
            </div>

            {!isCollapsed && (
                <div className="connected-equipment-content">
                    {filteredCategories.map(category => (
                        <EquipmentCategoryGroup
                            key={category.id}
                            category={category}
                            onToggleCollapse={toggleCategoryCollapse}
                            onCardClick={handleCardClick}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ConnectedEquipmentView;
